
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { logger, redactParams } from './common/utils/logger';
import Aa1HomePage from './aa1/Aa1HomePage';
import Aa1TermsConditionPage from './aa1/Aa1TermsConditionPage';
import Aa2HomePage from './aa2/Aa2HomePage';
import Aa2TermsConditionPage from './aa2/Aa2TermsConditionPage';
import Aa3HomePage from './aa3/Aa3HomePage';
import Aa3HomeSuccessPage from './aa3/Aa3HomeSuccessPage';
import Aa3TermsConditionPage from './aa3/Aa3TermsConditionPage';
import Aa4HomePage from './aa4/Aa4HomePage';
import Aa4TermsConditionPage from './aa4/Aa4TermsConditionPage';
import PinPage from './aa5/PinPage';
import SuccessPage from './aa5/SuccessPage';
import TermsPage from './aa5/TermsPage';
import Aa000HomePage from './aa000/Aa000HomePage';
import Aa000HomeSuccessPage from './aa000/Aa000HomeSuccessPage';
import Aa000TermsConditionPage from './aa000/Aa000TermsConditionPage';
import Page2208 from './2208/Page2208';
import Page2208Success from './2208/Page2208Success';
import Page3346 from './3346/Page3346';
import Page3346Success from './3346/Page3346Success';
import { HelmetProvider } from 'react-helmet-async';
import LpPreloads from "./LpPreloads";
import ResultPage from './common/ResultPage';

/**
 * Human-readable page name per route — e.g. "LP aa2208 — Success", "LP
 * aa4523 — PIN Entry" — so log lines read like a product person's
 * description of the page, not a raw path. A lookup table (not a regex,
 * unlike kw-lp-duplicate's numeric-only routes) since this app mixes
 * "aaN" and bare numbered ("2208"/"3346") route naming.
 */
const PAGE_NAMES = {
  "/aa1": "LP aa1 — Home",
  "/aa1/success": "LP aa1 — Success",
  "/aa1/failed": "LP aa1 — Failed",
  "/aa2": "LP aa2 — Home",
  "/aa2/success": "LP aa2 — Success",
  "/aa2/failed": "LP aa2 — Failed",
  "/aa3": "LP aa3 — Home",
  "/aa3/success": "LP aa3 — Success",
  "/aa3/failed": "LP aa3 — Failed",
  "/aa3/terms": "LP aa3 — Terms & Conditions",
  "/aa4": "LP aa4 — Home",
  "/aa4/success": "LP aa4 — Success",
  "/aa4/failed": "LP aa4 — Failed",
  "/aa4523": "LP aa4523 — PIN Entry",
  "/aa4523/terms": "LP aa4523 — Terms & Conditions",
  "/aa4523/success": "LP aa4523 — Success",
  "/aa4523/failed": "LP aa4523 — Failed",
  "/aa000": "LP aa000 — Home",
  "/aa000/terms": "LP aa000 — Terms & Conditions",
  "/aa000/success": "LP aa000 — Success",
  "/aa000/failed": "LP aa000 — Failed",
  "/aa2208": "LP aa2208 — Home",
  "/aa2208/success": "LP aa2208 — Success",
  "/aa2208/failed": "LP aa2208 — Failed",
  "/aa3346": "LP aa3346 — Home",
  "/aa3346/success": "LP aa3346 — Success",
  "/aa3346/failed": "LP aa3346 — Failed",
};

function resolvePageName(pathname) {
  return PAGE_NAMES[pathname] || pathname;
}

/**
 * Logs a page-view on every route change and sets the ambient page name
 * every subsequent log line will carry (see logger.setPageName) — mounted
 * once inside <Router> so every page component gets tracked without
 * needing its own logging call.
 */
function RouteViewLogger() {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname);
    logger.setPageName(pageName, location.pathname);

    logger.info("page_view", "route_change", `Visitor opened the "${pageName}" page`, {
      path: location.pathname,
      query: redactParams(Object.fromEntries(new URLSearchParams(location.search))),
    });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <RouteViewLogger />
        <LpPreloads />
        <Routes>
          <Route path="/aa1" element={<Aa1HomePage />} />
          <Route path="/aa1/terms" element={<Aa1TermsConditionPage />} />
          <Route path="/aa1/success" element={<ResultPage status="success" />} />
          <Route path="/aa1/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa2" element={<Aa2HomePage />} />
          <Route path="/aa2/terms" element={<Aa2TermsConditionPage />} />
          <Route path="/aa2/success" element={<ResultPage status="success" />} />
          <Route path="/aa2/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa3" element={<Aa3HomePage />} />
          <Route path="/aa3/success" element={<Aa3HomeSuccessPage />} />
          <Route path="/aa3/failed" element={<ResultPage status="failed" />} />
          <Route path="/aa3/terms" element={<Aa3TermsConditionPage />} />

          <Route path="/aa4" element={<Aa4HomePage />} />
          <Route path="/aa4/terms" element={<Aa4TermsConditionPage />} />
          <Route path="/aa4/success" element={<ResultPage status="success" />} />
          <Route path="/aa4/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa4523" element={<PinPage />} />
          <Route path="/aa4523/terms" element={<TermsPage />} />
          <Route path="/aa4523/success" element={<SuccessPage />} />
          <Route path="/aa4523/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa000" element={<Aa000HomePage />} />
          <Route path="/aa000/terms" element={<Aa000TermsConditionPage />} />
          <Route path="/aa000/success" element={<Aa000HomeSuccessPage />} />
          <Route path="/aa000/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa2208" element={<Page2208 />} />
          <Route path="/aa2208/success" element={<Page2208Success />} />
          <Route path="/aa2208/failed" element={<ResultPage status="failed" />} />

          <Route path="/aa3346" element={<Page3346 />} />
          <Route path="/aa3346/success" element={<Page3346Success />} />
          <Route path="/aa3346/failed" element={<ResultPage status="failed" />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;